import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { WidgetCard } from '../components/WidgetCard';
import { useNavigate, useParams } from 'react-router-dom';
import { MessageSquare, Camera, FileText, MapPin, Clock, User, FolderCheck, ArrowLeft, Loader2, Share2, Copy, Check, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import InstallPWA from '../components/InstallPWA';
import { DailyHoroscope } from '../components/DailyHoroscope';
import { AddToHomeScreen } from '../components/AddToHomeScreen';
import { DailyVideo } from '../components/DailyVideo';
import { UpcomingEvents } from '../components/UpcomingEvents';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const ClientDashboard = ({ user }) => {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [viewingClient, setViewingClient] = useState(null);
  const [loadingClient, setLoadingClient] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [mockups, setMockups] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [sharedAlbums, setSharedAlbums] = useState([]);
  const [clientAlbums, setClientAlbums] = useState([]);

  // Determine if admin is viewing a client's dashboard
  const isAdminViewing = (user.role === 'admin' || user.role === 'supervisor') && clientId;
  const displayUser = isAdminViewing ? viewingClient : user;

  useEffect(() => {
    if (isAdminViewing) {
      fetchClientData(clientId);
    } else if (user.role !== 'client') {
      navigate('/dashboard');
      return;
    } else {
      fetchProjects();
    }
  }, [user.role, clientId, isAdminViewing]);

  useEffect(() => {
    if (selectedProject) {
      fetchProjectDetails(selectedProject);
    }
  }, [selectedProject]);

  const fetchClientData = async (userId) => {
    setLoadingClient(true);
    try {
      const clientResponse = await axios.get(`${API}/users/${userId}`);
      const clientData = clientResponse.data;
      setViewingClient(clientData);
      
      // Fetch projects and filter for this specific client
      const projectsResponse = await axios.get(`${API}/projects`);
      const clientProjects = projectsResponse.data.filter(project => 
        // Check if client is in assigned_clients array
        project.assigned_clients && project.assigned_clients.includes(userId)
      );
      
      // Sort projects by most recent first (created_at descending)
      const sortedProjects = clientProjects.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      
      setProjects(sortedProjects);
      if (sortedProjects.length > 0) {
        setSelectedProject(sortedProjects[0].id);
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch client data:', error);
      toast.error('Failed to load client dashboard');
      navigate('/team');
    } finally {
      setLoadingClient(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      const clientProjects = response.data;
      
      // Sort projects by most recent first (created_at descending)
      const sortedProjects = clientProjects.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      
      setProjects(sortedProjects);
      if (sortedProjects.length > 0 && !selectedProject) {
        setSelectedProject(sortedProjects[0].id);
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch projects');
      setLoading(false);
    }
  };

  const fetchProjectDetails = async (projectId) => {
    try {
      // Fetch project details
      const projectRes = await axios.get(`${API}/projects/${projectId}`);
      setProjectData(projectRes.data);

      // Fetch photos
      const photosRes = await axios.get(`${API}/photos/${projectId}`);
      
      // Prioritize hero photo if one exists, otherwise sort by most recent
      const allPhotos = photosRes.data;
      const heroPhoto = allPhotos.find(p => p.is_hero_photo);
      let sortedPhotos;
      
      if (heroPhoto) {
        // Put hero photo first, then the rest
        sortedPhotos = [heroPhoto, ...allPhotos.filter(p => p.id !== heroPhoto.id)];
      } else {
        // Sort by timestamp descending (most recent first)
        sortedPhotos = allPhotos.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      }
      
      setPhotos(sortedPhotos.slice(0, 9)); // Latest 9 photos

      // Fetch mockup photos
      try {
        const mockupsRes = await axios.get(`${API}/projects/${projectId}/mockups`);
        setMockups(mockupsRes.data.slice(0, 6));
      } catch (e) {
        setMockups([]);
      }

      // Notes are not accessible to clients - removed

      // Fetch assigned contacts
      if (projectRes.data.assigned_contacts && projectRes.data.assigned_contacts.length > 0) {
        const usersRes = await axios.get(`${API}/users`);
        const contactUsers = usersRes.data.filter(u => 
          projectRes.data.assigned_contacts.includes(u.id)
        );
        setContacts(contactUsers);
      } else {
        // Default to all admins
        const usersRes = await axios.get(`${API}/users`);
        const adminUsers = usersRes.data.filter(u => u.role === 'admin');
        setContacts(adminUsers);
      }
      
      // Fetch shared photo albums for this user
      await fetchSharedAlbums();
      
      // Fetch client's own photo albums
      await fetchClientAlbums();
    } catch (error) {
      console.error('Failed to fetch project details');
    }
  };
  
  const fetchSharedAlbums = async () => {
    try {
      const response = await axios.get(`${API}/photo-albums/shared-with-me`);
      console.log('[ClientDashboard] Fetched shared albums:', response.data.length);
      setSharedAlbums(response.data);
    } catch (error) {
      console.error('[ClientDashboard] Failed to fetch shared albums:', error);
      setSharedAlbums([]);
    }
  };
  
  const fetchClientAlbums = async () => {
    try {
      const response = await axios.get(`${API}/photo-albums`);
      console.log('[ClientDashboard] Fetched client albums:', response.data.length);
      setClientAlbums(response.data);
    } catch (error) {
      console.error('[ClientDashboard] Failed to fetch client albums:', error);
      setClientAlbums([]);
    }
  };

  const handleMessageContact = (contactId) => {
    navigate('/messages', { state: { selectedUserId: contactId } });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { timeZone: 'America/Toronto', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleCopyShareLink = () => {
    if (!displayUser) return;
    const shareUrl = `${window.location.origin}/client-dashboard/${displayUser.id}`;
    navigator.clipboard.writeText(shareUrl);
    setLinkCopied(true);
    toast.success('Dashboard link copied to clipboard!');
    setTimeout(() => setLinkCopied(false), 2000);
  };

  if (loading || loadingClient) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20 pt-20 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          <p className="text-gray-600">{loadingClient ? 'Loading client dashboard...' : 'Loading your project...'}</p>
        </div>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20 pt-20 flex items-center justify-center">
        <div className="text-center">
          <Camera className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Projects Yet</h2>
          <p className="text-gray-600">You don&apos;t have any assigned projects. Contact your project manager.</p>
        </div>
      </div>
    );
  }

  const heroPhoto = photos[0] || null;

  return (
    <div className="min-h-screen bg-gray-50 pb-20 pt-20">
      {/* Admin Viewing Header */}
      {isAdminViewing && displayUser && (
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-md">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => navigate('/team')}
                  variant="outline"
                  size="sm"
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Team
                </Button>
                <div>
                  <p className="text-sm opacity-90">Viewing Dashboard for:</p>
                  <h2 className="text-xl font-bold">{displayUser.name}</h2>
                </div>
              </div>
              <Button
                onClick={handleCopyShareLink}
                size="sm"
                className="bg-white/10 border border-white/30 text-white hover:bg-white/20"
              >
                {linkCopied ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Share2 className="w-4 h-4 mr-2" />
                    Copy Dashboard Link
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Project Selector - Only show if multiple projects */}
      {!isAdminViewing && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4">
          <WidgetCard />
        </div>
      )}
      {projects.length > 1 && (
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className="w-full sm:w-96">
                <SelectValue placeholder="Select a project" />
              </SelectTrigger>
              <SelectContent>
                {projects.map(project => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {projectData && (
        <>
          {/* Hero Section - Full Width Banner */}
          <div className="relative h-96 w-full overflow-hidden">
            {/* Background Image */}
            {heroPhoto ? (
              <img
                src={`${BACKEND_URL}${heroPhoto.file_path}`}
                alt={projectData.name}
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-800" />
            )}
            
            {/* Overlay Gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent" />
            
            {/* Content Overlay */}
            <div className="absolute inset-0 flex items-end">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 w-full">
                <div className="text-white">
                  <h1 className="text-4xl sm:text-5xl font-bold mb-3">{projectData.name}</h1>
                  <div className="flex flex-wrap gap-4 text-sm sm:text-base mb-4">
                    {projectData.status && (
                      <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                        <Clock className="w-4 h-4" />
                        <span className="font-medium">{projectData.status}</span>
                      </div>
                    )}
                    {projectData.category && (
                      <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                        <MapPin className="w-4 h-4" />
                        <span>{projectData.category}</span>
                      </div>
                    )}
                  </div>
                  {projectData.description && (
                    <p className="text-lg max-w-3xl">{projectData.description}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Recent Photos & Notes */}
              <div className="lg:col-span-2 space-y-6">
                {/* Recent Photos */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-bold text-gray-900">Recent Photos</h2>
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/projects/${selectedProject}`)}
                      className="text-sm"
                    >
                      View All
                    </Button>
                  </div>
                  {photos.length > 0 ? (
                    <div className="grid grid-cols-3 gap-3">
                      {photos.map(photo => (
                        <div
                          key={photo.id}
                          className="aspect-square rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition"
                          onClick={() => navigate(`/projects/${selectedProject}`)}
                        >
                          <img
                            src={`${BACKEND_URL}${photo.file_path}`}
                            alt={photo.description || 'Project photo'}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No photos yet</p>
                  )}
                </div>

                {/* Project Photo Mockups */}
                {mockups.length > 0 && (
                  <div className="bg-white rounded-lg shadow-md p-6" data-testid="client-mockups-section">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-2xl font-bold text-gray-900">Project Photo Mockups</h2>
                      <Button
                        variant="outline"
                        onClick={() => navigate(`/projects/${selectedProject}`)}
                        className="text-sm"
                        data-testid="client-mockups-view-all"
                      >
                        View All
                      </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {mockups.map(m => (
                        <div
                          key={m.id}
                          className="aspect-square rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition relative"
                          onClick={() => navigate(`/projects/${selectedProject}`)}
                          data-testid={`client-mockup-${m.id}`}
                        >
                          <img
                            src={`${BACKEND_URL}${m.file_path}`}
                            alt={m.text || 'Mockup'}
                            className="w-full h-full object-cover"
                          />
                          {m.text && (
                            <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10px] px-1.5 py-0.5 truncate">{m.text}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Shared Photo Albums */}
                {sharedAlbums.length > 0 && (
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Camera className="w-6 h-6 text-blue-600" />
                        <h2 className="text-2xl font-bold text-gray-900">Shared Photo Albums</h2>
                      </div>
                      <Button
                        variant="outline"
                        onClick={() => navigate('/photo-albums')}
                        className="text-sm"
                      >
                        View All
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {sharedAlbums.slice(0, 4).map(album => (
                        <div
                          key={album.id}
                          className="border border-gray-200 rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                          onClick={() => navigate(`/photo-albums/${album.id}`)}
                        >
                          <div className="h-40 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                            <Camera className="w-12 h-12 text-white opacity-50" />
                          </div>
                          <div className="p-4">
                            <h3 className="font-semibold text-gray-900 mb-1">{album.name}</h3>
                            <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                              {album.description || 'No description'}
                            </p>
                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <span>{album.photo_count} photo{album.photo_count !== 1 ? 's' : ''}</span>
                              <span className="flex items-center gap-1">
                                <Share2 className="w-3 h-3" />
                                by {album.user_name}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Client's Personal Photo Gallery */}
                {!isAdminViewing && (
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Camera className="w-6 h-6 text-purple-600" />
                        <h2 className="text-2xl font-bold text-gray-900">My Photo Gallery</h2>
                      </div>
                      <Button
                        onClick={() => navigate('/photo-albums')}
                        className="bg-purple-600 hover:bg-purple-700 text-sm"
                      >
                        Manage Albums
                      </Button>
                    </div>
                    <p className="text-gray-600 mb-4 text-sm">
                      Create your own photo albums to share with your project team. Perfect for reference photos, inspiration, or documenting your needs.
                    </p>
                    {clientAlbums.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {clientAlbums.slice(0, 4).map(album => (
                          <div
                            key={album.id}
                            className="border border-gray-200 rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                            onClick={() => navigate(`/photo-albums/${album.id}`)}
                          >
                            <div className="h-40 bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                              <Camera className="w-12 h-12 text-white opacity-50" />
                            </div>
                            <div className="p-4">
                              <h3 className="font-semibold text-gray-900 mb-1">{album.name}</h3>
                              <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                                {album.description || 'No description'}
                              </p>
                              <div className="flex items-center justify-between text-xs text-gray-500">
                                <span>{album.photo_count} photo{album.photo_count !== 1 ? 's' : ''}</span>
                                <span className="text-purple-600 font-medium">Your Album</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                        <Camera className="w-16 h-16 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-600 mb-4">You haven't created any photo albums yet</p>
                        <Button
                          onClick={() => navigate('/photo-albums')}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Create Your First Album
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Client Notes from Admin */}
                <ClientNotesSection />
              </div>

              {/* Right Column - Quick Actions & Contacts */}
              <div className="space-y-6">
                {/* Upcoming Events */}
                <UpcomingEvents limit={3} userRole="client" />
                
                {/* Add to Home Screen - Only show for actual client */}
                {!isAdminViewing && <AddToHomeScreen />}

                {/* Daily Horoscope - Only show for actual client viewing their own dashboard */}
                {!isAdminViewing && <DailyHoroscope />}

                {/* Daily Video */}
                <DailyVideo />

                {/* Quick Message */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <MessageSquare className="w-6 h-6 text-blue-600" />
                    <h3 className="text-xl font-bold text-gray-900">Contact Team</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Have questions about your project? Message your team.
                  </p>
                  {contacts.length > 0 ? (
                    <div className="space-y-2">
                      {contacts.map(contact => (
                        <Button
                          key={contact.id}
                          onClick={() => handleMessageContact(contact.id)}
                          className="w-full bg-blue-600 hover:bg-blue-700 justify-start"
                        >
                          <User className="w-4 h-4 mr-2" />
                          Message {contact.name}
                        </Button>
                      ))}
                    </div>
                  ) : (
                    <Button
                      onClick={() => navigate('/messages')}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Open Messages
                    </Button>
                  )}
                </div>

                {/* Install App for Notifications */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <InstallPWA />
                </div>

                {/* Project Info Card */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Project Details</h3>
                  <div className="space-y-3 text-sm">
                    {projectData.timeline && (
                      <div>
                        <span className="text-gray-500">Timeline:</span>
                        <p className="text-gray-900 font-medium">{projectData.timeline}</p>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-500">Status:</span>
                      <p className="text-gray-900 font-medium">{projectData.status}</p>
                    </div>
                    {projectData.category && (
                      <div>
                        <span className="text-gray-500">Type:</span>
                        <p className="text-gray-900 font-medium">{projectData.category}</p>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-500">Total Photos:</span>
                      <p className="text-gray-900 font-medium">{photos.length}+ uploaded</p>
                    </div>
                  </div>
                </div>

                {/* Quick Links */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Links</h3>
                  <div className="space-y-2">
                    <Button
                      variant="outline"
                      onClick={() => navigate('/projects')}
                      className="w-full justify-start"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      All Projects
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate('/projects')}
                      className="w-full justify-start bg-green-50 hover:bg-green-100 border-green-200"
                    >
                      <FolderCheck className="w-4 h-4 mr-2 text-green-600" />
                      <span className="text-green-700 font-medium">Past Projects</span>
                    </Button>
                    {/* Photo Gallery link hidden per user request */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};


// Client Notes Component
const ClientNotesSection = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchClientNotes();
  }, []);

  const fetchClientNotes = async () => {
    try {
      const response = await axios.get(`${API}/client-notes`);
      setNotes(response.data);
    } catch (error) {
      console.error('Failed to fetch client notes:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-6 h-6 text-blue-600" />
          <h3 className="text-xl font-bold text-gray-900">Notes from Admin</h3>
        </div>
        <div className="text-center py-4 text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center gap-2 mb-4">
        <FileText className="w-6 h-6 text-blue-600" />
        <h3 className="text-xl font-bold text-gray-900">
          Notes from Admin
          {notes.length > 0 && (
            <span className="ml-2 text-sm font-normal text-gray-600">({notes.length})</span>
          )}
        </h3>
      </div>
      
      {notes.length > 0 ? (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {notes.map((note) => (
            <div
              key={note.id}
              className="p-4 bg-blue-50 rounded-lg border border-blue-100"
            >
              <div className="flex items-start justify-between mb-2">
                <span className="text-xs text-blue-600 font-medium">
                  {note.is_broadcast ? '📢 To All Clients' : '📝 Personal Note'}
                </span>
                <span className="text-xs text-gray-500">
                  {new Date(note.created_at).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })}
                </span>
              </div>
              <p className="text-gray-800 text-sm whitespace-pre-wrap">{note.content}</p>
              <p className="text-xs text-gray-500 mt-2">
                From: {note.created_by_name}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500 text-center py-8 text-sm">
          No notes from admin yet
        </p>
      )}
    </div>
  );
};


export default ClientDashboard;
